import axios from "axios";
import config from "core/config";
import { publishAuthLogout } from "core/authEvents";

const apiURL = config.API_BASE_URL;
const v1ApiURL = config.API_BASE_URL + "v1/";
const LOGIN_REQUEST_TIMEOUT_MS = 30000;
const UNSAFE_METHODS = new Set(["delete", "patch", "post", "put"]);

export const ensureCsrfCookie = async () => {
    const response = await axios.get(apiURL + "auth/csrf/", { withCredentials: true });
    const csrfToken = response.data?.csrfToken;
    if (!csrfToken) {
        throw new Error("The API did not provide a CSRF token.");
    }
    return csrfToken;
};

async function makeApiRequest(
    url,
    method = "get",
    headers = {},
    data = {},
    timeout = 0
) {
    const requestHeaders = { ...headers };
    if (UNSAFE_METHODS.has(method.toLowerCase())) {
        // The API can be hosted on a sibling subdomain. Its host-only CSRF
        // cookie is sent with credentials but cannot be read by this SPA, so
        // use the explicit token returned by the bootstrap endpoint.
        requestHeaders["X-CSRFToken"] = await ensureCsrfCookie();
    }

    const requestConfig = {
        method,
        url,
        headers: requestHeaders,
        data,
        maxBodyLength: Infinity,
        timeout,
        withCredentials: true,
    };

    return axios
        .request(requestConfig)
        .then((response) => response.data)
        .catch((error) => {
            if (error.response?.status === 401) {
                publishAuthLogout();
            }
            console.error(error);
            throw error;
        });
}

export const login = async (email, password, is_subscriber) =>
    await makeApiRequest(
        apiURL + "auth/login/",
        "post",
        {},
        { email, password, is_subscriber },
        LOGIN_REQUEST_TIMEOUT_MS
    );

// signup_channel "nhs" (the /auth/nhs-register page) makes the backend reject personal emails.
export const register = async (full_name, email, password, job_title, organisation, mobile_number, signup_channel) =>
    await makeApiRequest(
        apiURL + "auth/register/",
        "post",
        {},
        {
            full_name, email, password, job_title, organisation, mobile_number,
            ...(signup_channel ? { signup_channel } : {}),
        }
    );

export const activateAccount = async (uid, token) =>
    await makeApiRequest(
        apiURL + "auth/activate/" + uid + "/" + token,
        "get",
        {}
    );

export const verifyToken = async () => await makeApiRequest(apiURL + "auth/token-verify/", "post");
export const logout = async () => await makeApiRequest(apiURL + "auth/logout/", "post");
export const resetPassword = async (email) =>
    await makeApiRequest(apiURL + "auth/reset-password/", "post", {}, { email });
export const resetPasswordConfirm = async (uid, token, password) =>
    await makeApiRequest(
        apiURL + "auth/reset-password/confirm/",
        "post",
        {},
        { uid, token, new_password: password }
    );

export const allChatHistories = async () => await makeApiRequest(apiURL + "conversations/");
export const conversation = async (
    id = null,
    userMessage = null,
    pid = null,
    sid = null,
    mid = null,
    limit = 50,
    offset = 0
) => {
    const url = apiURL + "conversation/";
    if (id) {
        const queryParams = `limit=${limit}&offset=${offset}`;
        return await makeApiRequest(`${url}${id}?${queryParams}`);
    } else {
        const data = {
            message: userMessage,
            parent_id: pid,
            session_id: sid,
            message_id: mid,
        };
        return await makeApiRequest(
            v1ApiURL + "conversation/",
            "post",
            {},
            data
        );
    }
};

export const messageFeedback = async (feedbackData) =>
    await makeApiRequest(
        v1ApiURL + "conversation/message_feedback",
        "post",
        {},
        feedbackData
    );

export const allSettings = async () =>
    await makeApiRequest(v1ApiURL + "settings/", "get", {
        "Content-Type": "application/json",
    });

export const userSettings = async (method = "get", data = {}) =>
    await makeApiRequest(
        v1ApiURL + "me/",
        method,
        {},
        data
    );

export const feedbackPrompt = async (method = "get", data = {}) =>
    await makeApiRequest(
        v1ApiURL + "feedback/",
        method,
        {},
        data
    );
